import {
  createRef,
  FC,
  Fragment,
  HTMLAttributes,
  ReactNode,
  startTransition,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  flip,
  offset,
  Placement,
  shift,
  Strategy,
  useFloating,
} from '@floating-ui/react-dom'
import { Transition } from '@headlessui/react'
import { clsx } from 'clsx'

import { useDocument } from '../hooks/useDocument.js'
import { Portal } from '../Portal/index.js'
import {
  FloatingFullContext,
  useFloatingFullContext,
} from '../utils/floating/index.js'
import { TooltipContext, useTooltip } from './TooltipContext.js'

export type TooltipProps = {
  children: ReactNode
  placement?: Placement
  strategy?: Strategy
}

/**
 * Tooltips display informative text when users hover, focus on, or tap an element.
 *
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#tooltip
 */
export const Tooltip = ({
  children,
  placement = 'top',
  strategy = 'absolute',
}: TooltipProps) => {
  const { document } = useDocument()
  const [visible, setVisible] = useState(false)

  const { x, y, reference, floating, refs, update } = useFloating<HTMLElement>({
    placement,
    strategy,
    middleware: [offset(4), shift(), flip()],
  })

  const showTooltip = useCallback(() => {
    setVisible(true)
  }, [setVisible])

  const hideTooltip = useCallback(() => {
    setVisible(false)
  }, [setVisible])

  // Handle click outside
  useEffect(() => {
    const handleClickOutside: EventListener = event => {
      const target = event.composedPath?.()?.[0] || event.target
      if (target instanceof Node) {
        if (
          refs.floating.current != null &&
          refs.reference.current != null &&
          !refs.floating.current?.contains(target) &&
          !refs.reference.current?.contains(target)
        ) {
          hideTooltip()
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [refs.reference, refs.floating.current, hideTooltip])

  // Trigger: hover on trigger
  useEffect(() => {
    if (refs.reference.current == null) {
      return
    }

    refs.reference.current?.addEventListener('mouseenter', showTooltip)
    refs.reference.current?.addEventListener('mouseleave', hideTooltip)
    return () => {
      refs.reference.current?.removeEventListener('mouseenter', showTooltip)
      refs.reference.current?.removeEventListener('mouseleave', hideTooltip)
    }
  }, [refs.reference, showTooltip, hideTooltip])

  return (
    <TooltipContext.Provider
      value={{
        open: visible,
        onOpen: showTooltip,
        onClose: hideTooltip,
      }}
    >
      <FloatingFullContext.Provider
        value={{
          x,
          y,
          strategy,
          reference,
          floating,
          update,
        }}
      >
        {children}
      </FloatingFullContext.Provider>
    </TooltipContext.Provider>
  )
}
const TooltipTrigger: FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...props
}) => {
  const { reference } = useFloatingFullContext()

  return (
    <div ref={reference} {...props}>
      {children}
    </div>
  )
}

export type TooltipPanelProps = HTMLAttributes<HTMLDivElement> & {
  inverted?: boolean
}

const TooltipPanel: FC<TooltipPanelProps> = ({
  children,
  className,
  inverted = true,
}) => {
  const { open } = useTooltip()
  const { x, y, strategy, floating } = useFloatingFullContext()

  const panelRef = createRef<HTMLDivElement>()
  const arrowRef = createRef<HTMLDivElement>()

  const style = {
    position: strategy,
    top: '0',
    left: '0',
    transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
  }

  /**
   * @tag Vite migration
   * produces hydration error. As a workaround, component is initialized client-side only
   */
  const [isFirstRender, setIsFirstRender] = useState(true)
  useEffect(() => {
    startTransition(() => {
      setIsFirstRender(false)
    })
  }, [])

  if (isFirstRender || !children) {
    return null
  }

  return (
    <Portal>
      <div ref={panelRef} style={style} className={className}>
        <Transition
          show={open}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
          beforeEnter={() => floating(panelRef.current)}
          afterLeave={() => floating(null)}
        >
          <div
            className={clsx(
              'py-2 px-3',
              'w-max max-w-[calc(100vw-20px)] sm:max-w-md rounded-lg',
              'text-center text-xs pointer-events-none',
              inverted
                ? 'bg-surface-inverse text-content-on-inverse'
                : 'bg-surface text-content-subdued border border-line-subdued rounded-md shadow-lg',
            )}
          >
            {children}
            <div ref={arrowRef}>
              {/* <svg */}
              {/*  viewBox="0 0 255 255" */}
              {/*  xmlSpace="preserve" */}
              {/*  className="absolute text-content-subdued h-2 w-2" */}
              {/*  style={styles.arrow} */}
              {/* > */}
              {/*  <polygon className="fill-current" points="0,0 127.5,127.5 255,0" /> */}
              {/* </svg> */}
            </div>
          </div>
        </Transition>
      </div>
    </Portal>
  )
}

Tooltip.Trigger = TooltipTrigger
Tooltip.Panel = TooltipPanel
